import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SHIFT } from "../constants/testIds3";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Clock, X } from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Shifts() {
  const { user, tenantId } = useAuth();
  const canManage = ["super_admin", "company_admin", "hr"].includes(user?.role);
  const canRoster = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);
  const [shifts, setShifts] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openShift, setOpenShift] = useState(false);
  const [shiftForm, setShiftForm] = useState({ code: "", name: "", start_time: "09:00", end_time: "18:00", break_minutes: 60, weekdays: [0, 1, 2, 3, 4], color: "#047857" });
  const [openRoster, setOpenRoster] = useState(false);
  const [rosterForm, setRosterForm] = useState({ employee_id: "", shift_id: "", start_date: "", end_date: "" });

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/shifts", { params }).then(({ data }) => setShifts(data));
    api.get("/rosters", { params }).then(({ data }) => setRosters(data));
    api.get("/employees", { params }).then(({ data }) => setEmployees(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const submitShift = async () => {
    try {
      const payload = { ...shiftForm, break_minutes: Number(shiftForm.break_minutes) };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/shifts", payload);
      toast.success("Shift template added");
      setOpenShift(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitRoster = async () => {
    try {
      const payload = { ...rosterForm };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/rosters", payload);
      toast.success("Roster assigned");
      setOpenRoster(false); setRosterForm({ employee_id: "", shift_id: "", start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeRoster = async (id) => {
    if (!window.confirm("Delete this roster assignment?")) return;
    try { await api.delete(`/rosters/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleWeekday = (n) => {
    setShiftForm({ ...shiftForm, weekdays: shiftForm.weekdays.includes(n) ? shiftForm.weekdays.filter((x) => x !== n) : [...shiftForm.weekdays, n].sort() });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Time</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Shifts & Rosters</h1>
        <p className="text-sm text-zinc-500 mt-2">Templates for working hours and per-employee roster assignments.</p>
      </div>

      <Tabs defaultValue="shifts">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="shifts" className="rounded-sm">Shift templates</TabsTrigger>
          <TabsTrigger value="rosters" className="rounded-sm">Rosters</TabsTrigger>
        </TabsList>

        <TabsContent value="shifts" className="mt-4 space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={openShift} onOpenChange={setOpenShift}>
                <DialogTrigger asChild>
                  <Button className="btn-zenith rounded-sm" data-testid={SHIFT.createBtn}><Plus size={16} className="mr-1.5" /> New shift</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display text-2xl">New shift template</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Code *</Label><Input value={shiftForm.code} onChange={(e) => setShiftForm({ ...shiftForm, code: e.target.value.toUpperCase() })} className="rounded-sm font-mono-plex" data-testid="shift-code-input" /></div>
                    <div className="space-y-1"><Label className="text-xs">Color</Label><Input type="color" value={shiftForm.color} onChange={(e) => setShiftForm({ ...shiftForm, color: e.target.value })} className="rounded-sm h-10 p-1" /></div>
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Name *</Label><Input value={shiftForm.name} onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })} className="rounded-sm" data-testid="shift-name-input" /></div>
                    <div className="space-y-1"><Label className="text-xs">Start time</Label><Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} className="rounded-sm" data-testid="shift-start-input" /></div>
                    <div className="space-y-1"><Label className="text-xs">End time</Label><Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} className="rounded-sm" data-testid="shift-end-input" /></div>
                    <div className="space-y-1"><Label className="text-xs">Break (min)</Label><Input type="number" value={shiftForm.break_minutes} onChange={(e) => setShiftForm({ ...shiftForm, break_minutes: e.target.value })} className="rounded-sm" /></div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Working days</Label>
                      <div className="flex gap-1">
                        {DAYS.map((d, i) => (
                          <button key={d} type="button" onClick={() => toggleWeekday(i)} className={`text-xs px-2 py-1 rounded-sm border transition-colors duration-150 ${shiftForm.weekdays.includes(i) ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-zinc-300 text-zinc-700 hover:border-emerald-500"}`}>{d}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpenShift(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={submitShift} className="btn-zenith rounded-sm" data-testid={SHIFT.saveBtn}>Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={SHIFT.list}>
            {shifts.length === 0 && <div className="col-span-full border border-zinc-200 bg-white p-8 rounded-sm text-center text-sm text-zinc-500">No shift templates.</div>}
            {shifts.map((s) => (
              <div key={s.id} className="border border-zinc-200 rounded-sm p-4 bg-white">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm grid place-items-center" style={{ backgroundColor: (s.color || "#047857") + "22", color: s.color || "#047857" }}><Clock size={20} weight="duotone" /></div>
                    <div>
                      <div className="text-xs font-mono-plex text-zinc-500">{s.code}</div>
                      <div className="font-display text-lg font-bold">{s.name}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">{s.status}</Badge>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-600">
                  <span className="font-mono-plex">{s.start_time} → {s.end_time}</span>
                  <span>· {s.break_minutes}min break</span>
                </div>
                <div className="mt-2 flex gap-1">
                  {DAYS.map((d, i) => (
                    <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded-sm ${s.weekdays?.includes(i) ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-zinc-100 text-zinc-400 border border-zinc-200"}`}>{d}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="rosters" className="mt-4 space-y-4">
          {canRoster && (
            <div className="flex justify-end">
              <Dialog open={openRoster} onOpenChange={setOpenRoster}>
                <DialogTrigger asChild>
                  <Button className="btn-zenith rounded-sm" data-testid={SHIFT.rosterAssignBtn}><Plus size={16} className="mr-1.5" /> Assign roster</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display text-2xl">Assign roster</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                      <Select value={rosterForm.employee_id} onValueChange={(v) => setRosterForm({ ...rosterForm, employee_id: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="roster-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                        <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Shift *</Label>
                      <Select value={rosterForm.shift_id} onValueChange={(v) => setRosterForm({ ...rosterForm, shift_id: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="roster-shift-select"><SelectValue placeholder="Select shift" /></SelectTrigger>
                        <SelectContent>{shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.start_time}–{s.end_time}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Start</Label><Input type="date" value={rosterForm.start_date} onChange={(e) => setRosterForm({ ...rosterForm, start_date: e.target.value })} className="rounded-sm" /></div>
                    <div className="space-y-1"><Label className="text-xs">End</Label><Input type="date" value={rosterForm.end_date} onChange={(e) => setRosterForm({ ...rosterForm, end_date: e.target.value })} className="rounded-sm" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpenRoster(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={submitRoster} className="btn-zenith rounded-sm" data-testid="roster-save-btn">Assign</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="overflow-x-auto border border-zinc-200 rounded-sm" data-testid={SHIFT.rosterList}>
            <table className="hr-table">
              <thead><tr><th>Employee</th><th>Shift</th><th>Timing</th><th>From</th><th>To</th><th></th></tr></thead>
              <tbody>
                {rosters.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">No roster assignments yet.</td></tr>}
                {rosters.map((r) => (
                  <tr key={r.id}>
                    <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                    <td>
                      <div className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.shift_color || "#047857" }} />
                        <span className="text-sm">{r.shift_name}</span>
                        <span className="text-xs font-mono-plex text-zinc-500">· {r.shift_code}</span>
                      </div>
                    </td>
                    <td className="font-mono-plex text-xs">{r.shift_start_time} → {r.shift_end_time}</td>
                    <td className="font-mono-plex text-xs">{r.start_date}</td>
                    <td className="font-mono-plex text-xs">{r.end_date}</td>
                    <td>{canRoster && <button onClick={() => removeRoster(r.id)} className="text-xs text-red-600 hover:underline"><X size={12} className="inline" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
